import React, { useEffect, useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import {
  Briefcase, Building2, Users, ShieldCheck, Loader2, TrendingUp, ExternalLink,
} from 'lucide-react';
import PoPShell from './PoPShell';
import { usePoPAuth } from '../../context/PoPAuthContext';
import { popStats, roleLabel } from '../../lib/popApi';
import { ExpertDashboard, UniversityDashboard, FacultyDashboard, StudentDashboard, OrgDashboard, MicroStat } from './RoleDashboards';

const PoPDashboard = () => {
  const { user, loading } = usePoPAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);

  useEffect(() => {
    if (!loading && !user) { navigate('/pop', { replace: true }); return; }
    if (!loading && user && user.role === 'unassigned') { navigate('/pop/onboarding', { replace: true }); return; }
    if (!loading && user && user.role === 'super_admin') { navigate('/pop/admin', { replace: true }); return; }
  }, [user, loading, navigate]);

  useEffect(() => {
    if (!user) return;
    popStats().then(setStats).catch(() => {});
  }, [user]);

  if (loading || !user) {
    return (
      <PoPShell activeTab="dashboard">
        <div className="flex items-center justify-center py-24 text-white/60"><Loader2 className="w-5 h-5 animate-spin mr-2" /> Loading…</div>
      </PoPShell>
    );
  }

  const RoleView = {
    industry_expert: ExpertDashboard,
    university: UniversityDashboard,
    faculty: FacultyDashboard,
    student: StudentDashboard,
    industry_org: OrgDashboard,
  }[user.role] || StudentDashboard;

  return (
    <PoPShell activeTab="dashboard">
      {/* Header */}
      <div className="mb-8 flex items-start justify-between gap-4 flex-wrap">
        <div>
          <div className="text-[10px] tracking-[0.3em] font-bold text-amber-300 mb-2">{roleLabel(user.role).toUpperCase()}</div>
          <h1 className="font-serif text-3xl sm:text-4xl">Welcome, {user.name.split(' ')[0]}.</h1>
          <div className="text-sm text-white/60 mt-1">
            Verification: <span className={
              user.verification_status === 'verified' ? 'text-emerald-300 font-semibold' :
              user.verification_status === 'rejected' ? 'text-rose-300 font-semibold' :
              'text-amber-300 font-semibold'
            }>{user.verification_status}</span>
            {user.verification_status === 'unverified' && (
              <span className="text-[11px] text-white/40 ml-1">· Complete your profile to enter the verification queue.</span>
            )}
          </div>
        </div>
        <Link to="/pop/onboarding" className="hidden sm:inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md bg-white/5 hover:bg-white/10 text-xs text-white/80" data-testid="dashboard-edit-profile">
          Edit profile <ExternalLink className="w-3 h-3" />
        </Link>
      </div>

      {/* Ecosystem strip */}
      {stats && (
        <div className="grid grid-cols-2 sm:grid-cols-5 gap-2 mb-8">
          <MicroStat Icon={Users}       label="Members"       value={stats.total_members} />
          <MicroStat Icon={Briefcase}   label="Experts"       value={stats.industry_experts} />
          <MicroStat Icon={Building2}   label="Universities"  value={stats.universities} />
          <MicroStat Icon={ShieldCheck} label="Verified"      value={stats.verified_profiles} />
          <MicroStat Icon={TrendingUp}  label="Open Roles"    value={stats.open_opportunities} />
        </div>
      )}

      <RoleView user={user} />
    </PoPShell>
  );
};

export default PoPDashboard;
